<template>
  <div class="container">
    <!-- Header Section -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-icon">
            <b-icon icon="wifi"></b-icon>
          </div>
          <div class="header-text">
            <h1 class="page-title">Gestión de redes WiFi</h1>
            <p class="page-subtitle">Administra las redes WiFi por CBI del sistema</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Sección de configuración -->
    <div class="config-section">
      <div class="config-row">
        <div class="config-item">
          <label>API Key (header X-API-Key) — opcional si tu backend no la requiere</label>
          <input v-model="apiKey" type="password" placeholder="Ingresa tu API Key si está activada la protección" readonly />
          <small>Se guarda localmente (localStorage) en tu navegador para tu comodidad.</small>
        </div>
        <div class="config-item">
          <label>CBI del dispositivo</label>
          <input v-model="cbi" type="text" placeholder="Ej: 16550441187" />
          <small>Cada CBI tiene su propia versión y lista de redes.</small>
        </div>
      </div>
      
      <div class="config-bottom">
        <div class="actions">
          <button class="btn" @click="loadNetworks">Cargar redes (GET)</button>
          <button class="btn primary" @click="saveNetworks">Guardar redes (PUT)</button>
          <button class="btn warn" @click="saveNetworksAll">Guardar (todos)</button>
          <button class="btn" @click="addRow">Agregar red</button>
        </div>
        <div class="version-info">
          <label>
            <input type="checkbox" v-model="replace" /> 
            Reemplazar completamente (replace=true)
          </label>
          <span class="pill">versión actual: {{ currentVersion !== null && currentVersion !== undefined ? currentVersion : '—' }}</span>
        </div>
      </div>
    </div>

    <div v-if="status.message" class="status" :class="status.kind">{{ status.message }}</div>

    <table class="net-table">
      <thead>
        <tr>
          <th>SSID</th>
          <th>Password</th>
          <th>Prioridad</th>
          <th>Habilitada</th>
          <th>Acciones</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(n, idx) in networks" :key="idx">
          <td><input v-model="n.ssid" type="text" placeholder="SSID" /></td>
          <td><input v-model="n.password" type="text" placeholder="Password" /></td>
          <td><input v-model.number="n.priority" type="number" step="1" /></td>
          <td><input v-model="n.enabled" type="checkbox" /></td>
          <td class="right"><button class="btn error" @click="removeRow(idx)">Eliminar</button></td>
        </tr>
      </tbody>
    </table>

    <!-- Sección de notas -->
    <div class="notes-section">
      <h3>Notas:</h3>
      <ul>
        <li>"<strong>Guardar redes (PUT)</strong>" llama a <code>POST /api/wifi/put</code> con <code>{"CBI","networks","replace"}</code>.</li>
        <li>"<strong>Guardar (todos)</strong>" llama a <code>POST /api/wifi/put_all</code> con <code>{"networks","replace"}</code> y publica credenciales globales.</li>
        <li>"<strong>Cargar redes (GET)</strong>" llama a <code>GET /api/wifi/get?cbi=...</code> y llena la tabla para ese CBI.</li>
        <li>Si definiste <code>API_KEY</code> en el servidor, se enviará como header <code>x-API-Key</code>.</li>
      </ul>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
export default {
  name: 'WifiManager',
  data() {
    return {
      cbi: '',
      apiKey: 'S1N4RQ20253N3RV4',
      replace: true,
      currentVersion: 0,
      networks: [],
      status: {
        kind: '',
        message: ''
      }
    };
  },
  mounted() {
    const savedCbi = localStorage.getItem('wifi_ui_cbi');
    const savedApiKey = localStorage.getItem('wifi_ui_api_key');
    if (savedCbi) this.cbi = savedCbi;
    if (savedApiKey) this.apiKey = savedApiKey;
    this.addRow();
  },
  methods: {
    showStatus(kind, msg) {
      this.status.kind = kind;
      this.status.message = msg;
    },
    clearStatus() {
      this.status.kind = '';
      this.status.message = '';
    },
    setVersion(v) {
      this.currentVersion = v !== null && v !== undefined ? v : 0;
    },
    addRow(net = { ssid: '', password: '', priority: 0, enabled: true }) {
      this.networks.push({ ...net });
    },
    removeRow(idx) {
      this.networks.splice(idx, 1);
    },
    collectNetworks() {
      return this.networks
        .map(n => ({
          ssid: String(n.ssid || '').trim(),
          password: String(n.password || ''),
          priority: !isNaN(+n.priority) && isFinite(+n.priority) ? +n.priority : 0,
          enabled: !!n.enabled,
        }))
        .filter(n => n.ssid.length > 0);
    },
    buildHeaders() {
      const h = { 'Content-Type': 'application/json'};
      const key = this.apiKey.trim();
      if (key) h['X-API-Key'] = key;
      return h;
    },
    async loadNetworks() {
      this.clearStatus();
      if (!this.cbi.trim()) {
        return this.showStatus('warn', 'Debes ingresar un CBI para cargar redes.');
      }
      localStorage.setItem('wifi_ui_cbi', this.cbi.trim());
      localStorage.setItem('wifi_ui_api_key', this.apiKey.trim());

      try {
        const res = await axios.get(`/api/wifi/get?cbi=${encodeURIComponent(this.cbi.trim())}`, {
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": this.apiKey.trim()
          }
        });
        
        if (res.status === 401) return this.showStatus('err', '401 unauthorized: verifica tu API Key (X-API-Key).');
        if (!res.status) return this.showStatus('err', `Error al cargar: ${res.status} ${await res.data}`);
        const data = await res.data;
        this.networks.splice(0, this.networks.length, ...(data.networks || []));
        if ((data.networks || []).length === 0) this.addRow();
        this.setVersion(data.version !== null && data.version !== undefined ? data.version : 0);
        this.showStatus('ok', 'Redes cargadas correctamente.');
      } catch (e) {
        this.showStatus('err', 'Fallo de red al cargar: ' + (e && e.message ? e.message : e));
      }
    },
    async saveNetworks() {
      this.clearStatus();
      if (!this.cbi.trim()) {
        return this.showStatus('warn', 'Debes ingresar un CBI para guardar redes.');
      }
      const nets = this.collectNetworks();
      if (nets.length === 0) return this.showStatus('warn', 'Debes ingresar al menos un SSID.');

      localStorage.setItem('wifi_ui_cbi', this.cbi.trim());
      localStorage.setItem('wifi_ui_api_key', this.apiKey.trim());

      const body = { CBI: this.cbi.trim(), networks: nets, replace: !!this.replace };
      try {
        const res = await axios.post('/api/wifi/put', JSON.stringify(body), {
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": this.apiKey.trim()
          }
        });
        if (res.status === 401) return this.showStatus('err', '401 unauthorized: verifica tu API Key (X-API-Key).');
        console.log(res);
        // const text = await res.text();
        let data;
        try {
          data = res.data;
        } catch (error) {
          data = null;
        }
        if (!res.data.status || res.data.status !== 'ok') return this.showStatus('err', `Error al guardar: ${res.status} ${res.data}`);
        const version = data && data.version !== null && data.version !== undefined ? data.version : '—';
        this.setVersion(version);
        this.showStatus('ok', `Guardado OK. Nueva versión: ${version}`);
      } catch (e) {
        this.showStatus('err', 'Fallo de red al guardar: ' + (e && e.message ? e.message : e));
      }
    },
    async saveNetworksAll() {
      this.clearStatus();
      const nets = this.collectNetworks();
      if (nets.length === 0) return this.showStatus('warn', 'Debes ingresar al menos un SSID.');

      localStorage.setItem('wifi_ui_api_key', this.apiKey.trim());

      const body = { networks: nets, replace: !!this.replace };
      try {
        const res = await axios.post('/api/wifi/put-all', JSON.stringify(body), {
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": this.apiKey.trim()
          }
        });
        if (res.status === 401) return this.showStatus('err', '401 unauthorized: verifica tu API Key (X-API-Key).');
        // const text = await res.text();
        let data;
        try {
          data = res.data;
        } catch (error) {
          data = null;
        }
        if (!res.status) return this.showStatus('err', `Error al guardar global: ${res.status} ${res.data}`);
        const version = data && data.version !== null && data.version !== undefined ? data.version : '—';
        this.setVersion(version);
        this.showStatus('ok', `Guardado Global OK. Versión: ${version}`);
      } catch (e) {
        this.showStatus('err', 'Fallo de red al guardar global: ' + (e && e.message ? e.message : e));
      }
    }
  }
};
</script>

<style scoped>
/* Container principal */
.container {
    max-width: 1400px;
    margin: 2rem auto;
    padding: 2rem;
    min-height: 100vh;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

/* Header Section */
.page-header {
    background: white;
    border-radius: 20px;
    padding: 2rem;
    margin-bottom: 2rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 1.5rem;
}

.header-icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    box-shadow: 0 8px 20px rgba(26, 39, 50, 0.3);
    font-size: 2rem;
}

.header-text {
    display: flex;
    flex-direction: column;
}

.page-title {
    color: #2d3748;
    font-size: 2.5rem;
    font-weight: 700;
    margin: 0;
    text-shadow: 2px 2px 4px rgba(45, 55, 72, 0.1);
}

.page-subtitle {
    color: #718096;
    font-size: 1.1rem;
    margin: 0.5rem 0 0 0;
    font-weight: 400;
}

/* Sección de configuración */
.config-section {
    background: white;
    border-radius: 15px;
    padding: 2rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.config-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    margin-bottom: 2rem;
}

.config-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.config-item label {
    font-weight: 600;
    color: #2d3748;
    font-size: 0.9rem;
}

.config-item input[type="text"] {
    padding: 0.75rem 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: white;
}

.config-item input[type="text"]:focus {
    outline: none;
    border-color: #1a2732;
    box-shadow: 0 0 0 3px rgba(26, 39, 50, 0.1);
}

.config-item input[type="password"] {
    padding: 0.75rem 1rem;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: white;
    letter-spacing: 0.2em;
}

.config-item input[type="password"]:focus {
    outline: none;
    border-color: #1a2732;
    box-shadow: 0 0 0 3px rgba(26, 39, 50, 0.1);
}

.config-item small {
    color: #6c757d;
    font-size: 0.8rem;
    line-height: 1.3;
}

.config-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #e2e8f0;
    flex-wrap: wrap;
}

.version-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-shrink: 0;
}

.version-info label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
    color: #2d3748;
    font-size: 0.9rem;
    white-space: nowrap;
}

.version-info input[type="checkbox"] {
    transform: scale(1.2);
}

/* Pill de versión */
.pill {
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 25px;
    margin-left: auto;
    font-weight: 600;
    font-size: 0.85rem;
    box-shadow: 0 4px 15px rgba(26, 39, 50, 0.3);
}

/* Acciones */
.actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: nowrap;
    flex: 1;
    align-items: center;
}

/* Botones */
.btn {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 15px;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.btn {
    background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
    color: #4a5568;
    border: 2px solid #cbd5e0;
}

.btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.btn.primary {
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
    color: white;
    border: 2px solid #1a2732;
}

.btn.primary:hover {
    background: linear-gradient(135deg, #2a3f4f 0%, #3a4f5f 100%);
    box-shadow: 0 6px 20px rgba(26, 39, 50, 0.3);
}

.btn.warn {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    color: white;
    border: 2px solid #f59e0b;
}

.btn.warn:hover {
    background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
    box-shadow: 0 6px 20px rgba(245, 158, 11, 0.3);
}

.btn.error {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    border: 2px solid #ef4444;
}

.btn.error:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.3);
}

/* Estados */
.status {
    margin-bottom: 1.5rem;
    padding: 1rem 1.5rem;
    border-radius: 15px;
    font-weight: 600;
    font-size: 0.95rem;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.status.ok {
    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
    color: #155724;
    border: 2px solid #c3e6cb;
}

.status.warn {
    background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
    color: #856404;
    border: 2px solid #ffeaa7;
}

.status.err {
    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
    color: #721c24;
    border: 2px solid #f5c6cb;
}

/* Tabla de redes */
.net-table {
    width: 100%;
    margin-bottom: 2rem;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-collapse: collapse;
    background: white;
}

.net-table thead {
    background: linear-gradient(135deg, #1a2732 0%, #2a3f4f 100%);
}

.net-table th {
    padding: 1.2rem 1rem;
    color: white;
    font-weight: 600;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border: none;
}

.net-table td {
    padding: 1rem;
    border-bottom: 1px solid #e2e8f0;
    vertical-align: middle;
}

.net-table tbody tr {
    transition: all 0.3s ease;
}

.net-table tbody tr:hover {
    background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
}

.net-table input[type="text"],
.net-table input[type="number"] {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.9rem;
    transition: all 0.3s ease;
}

.net-table input[type="text"]:focus,
.net-table input[type="number"]:focus {
    outline: none;
    border-color: #1a2732;
    box-shadow: 0 0 0 3px rgba(26, 39, 50, 0.1);
}

.net-table input[type="checkbox"] {
    transform: scale(1.3);
}

.right {
    text-align: right;
}

/* Sección de notas */
.notes-section {
    background: white;
    border-radius: 15px;
    padding: 2rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.2);
}

.notes-section h3 {
    color: #2d3748;
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0 0 1rem 0;
}

.notes-section ul {
    margin: 0;
    padding-left: 1.5rem;
}

.notes-section li {
    color: #4a5568;
    font-size: 0.9rem;
    line-height: 1.6;
    margin-bottom: 0.75rem;
}

.notes-section code {
    background: #f7fafc;
    color: #1a2732;
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 0.85rem;
}

/* Responsive */
@media (max-width: 768px) {
    .container {
        margin: 1rem;
        padding: 1rem;
    }
    
    .page-header {
        padding: 1.5rem;
    }
    
    .header-left {
        flex-direction: column;
        text-align: center;
        gap: 1rem;
    }
    
    .header-icon {
        width: 60px;
        height: 60px;
        font-size: 1.5rem;
        margin: 0 auto;
    }
    
    .page-title {
        font-size: 2rem;
    }
    
    .page-subtitle {
        font-size: 1rem;
    }
    
    .config-row {
        grid-template-columns: 1fr;
        gap: 1.5rem;
        margin-bottom: 1.5rem;
    }
    
    .config-bottom {
        flex-direction: column;
        align-items: stretch;
        gap: 1.5rem;
    }
    
    .actions {
        flex-direction: row;
        flex-wrap: wrap;
        gap: 0.75rem;
        justify-content: center;
    }
    
    .version-info {
        justify-content: center;
        flex-direction: column;
        gap: 0.75rem;
        text-align: center;
    }
    
    .net-table th,
    .net-table td {
        padding: 0.75rem 0.5rem;
        font-size: 0.85rem;
    }
    
    .notes-section {
        padding: 1.5rem;
    }
    
    .notes-section h3 {
        font-size: 1.1rem;
    }
    
    .notes-section li {
        font-size: 0.85rem;
    }
}
</style>